/**
 * Toast — MUI Snackbar with auto-dismiss + manual dismiss.
 *
 * Matches the ora app's ToastNotification visual style:
 *   variant="standard" (light background + colored left accent, not filled)
 *   anchorOrigin defaults to top-center (adapted from ora's top-right for
 *   the narrow taskpane width).
 *
 * When severity === "error" the body includes `correlation_id` so support
 * can triage from the toast text alone. Blocking failures use ErrorPanel.
 */
import { useCallback } from "react";
import { Snackbar, Alert } from "@mui/material";
import { toUserMessage } from "../utils/userErrorMessage";

export type ToastSeverity = "error" | "warning" | "info" | "success";

export interface ToastProps {
  open: boolean;
  message: string;
  severity?: ToastSeverity;
  /** Show this correlation_id when severity === "error". */
  correlationId?: string | null;
  /**
   * Auto-dismiss timeout in ms. Default 6000 (errors), 5000 (warnings),
   * 4000 (info/success). Pass `null` for no auto-dismiss (manual close only).
   */
  autoHideDurationMs?: number | null;
  onClose: () => void;
  /** Anchor — defaults to top-center for the narrow task pane. */
  anchorOrigin?: {
    vertical: "top" | "bottom";
    horizontal: "left" | "center" | "right";
  };
}

const DEFAULT_DURATION_BY_SEVERITY: Record<ToastSeverity, number> = {
  error: 6000,
  warning: 5000,
  info: 4000,
  success: 4000,
};

export default function Toast({
  open,
  message,
  severity = "info",
  correlationId,
  autoHideDurationMs,
  onClose,
  anchorOrigin = { vertical: "top", horizontal: "center" },
}: ToastProps) {
  const handleClose = useCallback(
    (_e?: unknown, reason?: string) => {
      if (severity === "error" && reason === "clickaway") return;
      onClose();
    },
    [severity, onClose],
  );

  const duration =
    autoHideDurationMs === null
      ? null
      : (autoHideDurationMs ?? DEFAULT_DURATION_BY_SEVERITY[severity]);

  const showCorrelation = severity === "error" && !!correlationId;

  return (
    <Snackbar
      open={open}
      autoHideDuration={duration ?? undefined}
      onClose={handleClose}
      anchorOrigin={anchorOrigin}
    >
      <Alert
        severity={severity}
        variant="standard"
        onClose={onClose}
        sx={{ maxWidth: "min(100%, 22rem)", width: "100%" }}
      >
        <div className="flex flex-col gap-1">
          <span className="text-sm">
            {severity === "error" ? toUserMessage(message) : message}
          </span>
          {showCorrelation && (
            <span className="text-xs opacity-75">
              ID: <code className="font-mono">{correlationId}</code>
            </span>
          )}
        </div>
      </Alert>
    </Snackbar>
  );
}
